import { useQuery } from '@tanstack/react-query'
import { useRecoilValue } from 'recoil'

import { useNodeApi } from '../api-provider'
import { selectedRoleState } from '../state'
import useControlPlaneApi from './use-control-plane-api'

export type TaskRunExecutionStatus = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'TIMED_OUT' | 'ABORTED' | 'PENDING_REDRIVE'
// Copy/pasted from API
interface Dataset {
  connectionId?: string
  identifier: string
  type: string
}
interface Artifact {
  s3Uri: string
  temporaryUrl: string
}

export interface TaskRun {
  status: TaskRunExecutionStatus
  startTime: string
  endTime: string
  errorMessage?: string
  destination: Dataset
  sources: Dataset[]
  artifacts?: Artifact[]
}

export const useListTaskRuns = ({
  typeFilter,
  tableId,
  statusFilter,
  limit = 10,
  nextToken,
  enabled = true,
}: {
  typeFilter?: string
  tableId?: string
  statusFilter?: string
  limit?: number
  nextToken?: string
  enabled?: boolean
}) => {
  const client = useControlPlaneApi()
  const nodeApi = useNodeApi()
  const { uniName, urqlClientNodeName: nodeName } = nodeApi
  const selectedRoleName = useRecoilValue(selectedRoleState).name

  return useQuery({
    queryKey: ['TaskRuns', uniName, nodeName, selectedRoleName, tableId, statusFilter, limit, nextToken],
    queryFn: async () => {
      const result = await nodeApi?.urqlClient
        ?.query(listTaskRunsQuery(typeFilter, tableId, statusFilter, limit, nextToken), {})
        .toPromise()
      if (result?.error) {
        throw new Error(result.error?.message)
      }
      const taskRuns = result?.data?.listVendia_TaskRunItems?.Vendia_TaskRunItems ?? []
      const resultNextToken = result?.data?.listVendia_TaskRunItems?.nextToken

      return {
        taskRuns,
        nextToken: resultNextToken,
      }
    },
    enabled:
      enabled && Boolean(client) && Boolean(nodeApi?.urqlClient) && Boolean(uniName) && Boolean(selectedRoleName),
    refetchOnMount: true,
  })
}

const listTaskRunsQuery = (
  typeFilter?: string,
  tableId?: string,
  statusFilter?: string,
  limit?: number,
  nextToken?: string,
) => {
  const filters = []

  if (typeFilter) {
    filters.push(`{ destination: { type: { eq: "${typeFilter}" } } }`)
  }

  if (tableId) {
    filters.push(`{ destination: { identifier: { eq: "${tableId}" } } }`)
  }

  if (statusFilter) {
    filters.push(`{ status: { eq: "${statusFilter}" } }`)
  }

  let filter = ''
  if (filters.length === 1) {
    filter = `filter: ${filters[0]}`
  } else if (filters.length > 1) {
    filter = `filter: { _and: [${filters.join(', ')}] }`
  }

  const queryArgs = []
  if (filter) queryArgs.push(filter)
  if (limit) queryArgs.push(`limit: ${limit}`)
  if (nextToken) queryArgs.push(`nextToken: "${nextToken}"`)
  queryArgs.push('order: { _id: DESC }')

  return /* GraphQL */ `
    query ListTaskRuns {
      listVendia_TaskRunItems(
        ${queryArgs.join('\n        ')}
      ) {
        Vendia_TaskRunItems {
          _id
          _owner
          artifacts {
            s3Uri
            temporaryUrl
          }
          destination {
            connectionId
            identifier
            type
          }
          endTime
          errorMessage
          executionId
          script
          sources {
            connectionId
            identifier
            type
          }
          startTime
          status
          task {
            _id
          }
        }
        nextToken
      }
    }
  `
}
